import type { ExhibitStyle } from "../types/exhibit_formats";
import { toLetterSequence, toRomanNumeral } from "./string_helpers";

/**
 * The one place exhibit numbers are turned into text. Previews and the live
 * exhibit list both go through it, so a format can never look different from
 * what it produces — notably around prefixes, which replace the punctuation
 * rather than sitting alongside it ("Exhibit A", not "Exhibit A.").
 */
export const formatExhibitToken = (
  index: number,
  style: ExhibitStyle | undefined
): string => {
  if (!style || style.token === "NONE") return "";

  let formattedNumber: string;

  switch (style.token) {
    case "NUMERIC":
      formattedNumber = `${index}`;
      break;
    case "LETTER":
      formattedNumber = toLetterSequence(index);
      if (style.case === "LOWER") {
        formattedNumber = formattedNumber.toLowerCase();
      }
      break;
    case "ROMAN_NUMERAL":
      formattedNumber = toRomanNumeral(index);
      if (style.case === "LOWER") {
        formattedNumber = formattedNumber.toLowerCase();
      }
      break;
    default:
      formattedNumber = `${index}`;
  }

  if (style.prefix) return `${style.prefix} ${formattedNumber}`;
  return `${formattedNumber}${style.punctuation}`;
};

/**
 * One level's token with no prefix and no punctuation, which is what dotted
 * labels are built from. "B.1.2" carries no punctuation between levels, and
 * adding it per token would produce "B..1..2".
 */
export const formatExhibitTokenBare = (
  index: number,
  style: ExhibitStyle | undefined
): string => {
  if (!style || style.token === "NONE") return "";

  switch (style.token) {
    case "LETTER": {
      const letters = toLetterSequence(index);
      return style.case === "LOWER" ? letters.toLowerCase() : letters;
    }
    case "ROMAN_NUMERAL": {
      const roman = toRomanNumeral(index);
      return style.case === "LOWER" ? roman.toLowerCase() : roman;
    }
    default:
      return `${index}`;
  }
};

/** The first `count` tokens of a level, for previews. Empty for unnumbered. */
export const formatLevelSamples = (
  style: ExhibitStyle | undefined,
  count = 3
): string[] => {
  if (!style || style.token === "NONE") return [];
  return Array.from({ length: count }, (_, i) =>
    formatExhibitToken(i + 1, style)
  );
};

export const isNumberedExhibitStyle = (style: ExhibitStyle | undefined) =>
  Boolean(style && style.token !== "NONE");
